import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  TextField,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Avatar,
  Menu,
  TablePagination,
  LinearProgress,
  FormHelperText
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  Visibility as ViewIcon,
  Edit as EditIcon,
  Call as CallIcon,
  Email as EmailIcon,
  MoreVert as MoreIcon,
  Person as PersonIcon,
  Business as BusinessIcon,
  TrendingUp as TrendingUpIcon,
  Star as StarIcon,
} from '@mui/icons-material';
import { useLocation } from '../../../contexts/LocationContext';
import api from '../../../utils/apiConfig';

const Leads = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [sourceFilter, setSourceFilter] = useState('');
  const [selectedLead, setSelectedLead] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState({
    totalLeads: 0,
    hotLeads: 0,
    conversionRate: 0,
    pipelineValue: 0
  });
  const [addOpen, setAddOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    type: 'individual',
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    company: '',
    source: 'website',
    budget: '',
    notes: ''
  });
  const [formError, setFormError] = useState('');
  
  const { selectedLocation } = useLocation();

  // Load leads data
  const loadLeads = async () => {
    if (!selectedLocation?._id) return;
    
    try {
      setLoading(true);
      setError(null);
      
      const response = await api.get(`/marketing/leads?locationId=${selectedLocation._id}&page=${page + 1}&limit=${rowsPerPage}${searchTerm ? `&search=${searchTerm}` : ''}${statusFilter ? `&status=${statusFilter.toLowerCase()}` : ''}${sourceFilter ? `&source=${sourceFilter.toLowerCase()}` : ''}`);
      
      if (response.data.success) {
        const leadsData = response.data.data.leads.map(lead => ({
          id: lead.id,
          name: `${lead.firstName} ${lead.lastName}`,
          email: lead.email,
          phone: lead.phone,
          company: lead.company || 'N/A',
          status: lead.status === 'HOT' ? 'Hot' : lead.status === 'WARM' ? 'Warm' : 'Cold',
          source: lead.source || 'Unknown',
          score: lead.score || 0,
          lastContact: lead.lastActivity ? new Date(lead.lastActivity).toLocaleDateString() : 'Never',
          nextFollowUp: lead.nextFollowUp ? new Date(lead.nextFollowUp).toLocaleDateString() : 'Not scheduled',
          notes: lead.notes || '',
          estimatedValue: lead.value || 0,
        }));
        setLeads(leadsData);
      }
    } catch (err) {
      console.error('Error loading leads:', err);
      setError('Failed to load leads');
      setLeads([]);
    } finally {
      setLoading(false);
    }
  };
  
  // Load stats
  const loadStats = async () => {
    if (!selectedLocation?._id) return;
    
    try {
      const response = await api.get(`/marketing/leads/stats/${selectedLocation._id}`);
      if (response.data.success) {
        const statsData = response.data.data.stats;
        setStats({
          totalLeads: statsData.totalLeads,
          hotLeads: statsData.newLeads + statsData.qualifiedLeads,
          conversionRate: `${statsData.conversionRate}%`,
          pipelineValue: `$${statsData.totalRevenue.toLocaleString()}`
        });
      }
    } catch (err) {
      console.error('Error loading stats:', err);
    }
  };
  
  // Load data when location or filters change
  useEffect(() => {
    loadLeads();
    loadStats();
  }, [selectedLocation, page, rowsPerPage, searchTerm, statusFilter, sourceFilter]);

  const statsCards = [
    {
      title: 'Total Leads',
      value: stats.totalLeads.toString(),
      icon: <PersonIcon sx={{ fontSize: 40, color: 'primary.main' }} />,
    },
    {
      title: 'Hot Leads',
      value: stats.hotLeads.toString(),
      icon: <StarIcon sx={{ fontSize: 40, color: 'error.main' }} />,
    },
    {
      title: 'Conversion Rate',
      value: stats.conversionRate,
      icon: <TrendingUpIcon sx={{ fontSize: 40, color: 'success.main' }} />,
    },
    {
      title: 'Pipeline Value',
      value: stats.pipelineValue,
      icon: <BusinessIcon sx={{ fontSize: 40, color: 'info.main' }} />,
    },
  ];

  const getStatusColor = (status) => {
    switch (status) {
      case 'Hot':
        return 'error';
      case 'Warm':
        return 'warning';
      case 'Cold':
        return 'info';
      default:
        return 'default';
    }
  };

  const getScoreColor = (score) => {
    if (score >= 80) return 'success';
    if (score >= 60) return 'warning';
    return 'error';
  };

  // Show loading or error states
  if (!selectedLocation) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <Typography variant="h6">Please select a location to view leads</Typography>
      </Box>
    );
  }
  
  if (error) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <Typography variant="h6" color="error">Error: {error}</Typography>
        <Button onClick={loadLeads} sx={{ mt: 2 }}>Retry</Button>
      </Box>
    );
  }

  const handleDetailsOpen = (lead) => {
    setSelectedLead(lead);
    setDetailsOpen(true);
    setAnchorEl(null);
  };

  const handleMenuOpen = (event, lead) => {
    setAnchorEl(event.currentTarget);
    setSelectedLead(lead);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedLead(null);
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold' }}>
          Lead Management
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          size="large"
          sx={{ fontWeight: 'bold' }}
          onClick={() => setAddOpen(true)}
        >
          Add Lead
        </Button>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {statsCards.map((stat, index) => (
          <Grid item xs={12} sm={6} md={3} key={index}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                  {stat.icon}
                </Box>
                <Typography variant="h4" component="div" sx={{ fontWeight: 'bold', mb: 1 }}>
                  {stat.value}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {stat.title}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Card>
        <CardContent>
          {/* Search and Filters */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                placeholder="Search leads..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={statusFilter}
                  label="Status"
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <MenuItem value="">All Status</MenuItem>
                  <MenuItem value="Hot">Hot</MenuItem>
                  <MenuItem value="Warm">Warm</MenuItem>
                  <MenuItem value="Cold">Cold</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth>
                <InputLabel>Source</InputLabel>
                <Select
                  value={sourceFilter}
                  label="Source"
                  onChange={(e) => setSourceFilter(e.target.value)}
                >
                  <MenuItem value="">All Sources</MenuItem>
                  <MenuItem value="Website">Website</MenuItem>
                  <MenuItem value="Social Media">Social Media</MenuItem>
                  <MenuItem value="Referral">Referral</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          {/* Leads Table */}
          <TableContainer component={Paper} variant="outlined">
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold' }}>Lead</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Company</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Score</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Source</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Value</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Next Follow-up</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={8} sx={{ textAlign: 'center', py: 4 }}>
                      <Typography>Loading leads...</Typography>
                    </TableCell>
                  </TableRow>
                ) : leads.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} sx={{ textAlign: 'center', py: 4 }}>
                      <Typography>No leads found</Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  leads.map((lead) => (
                    <TableRow key={lead.id} hover>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Avatar sx={{ mr: 2, backgroundColor: 'primary.main' }}>
                            {lead.name.charAt(0)}
                          </Avatar>
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                              {lead.name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {lead.email}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{lead.company}</Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={lead.status}
                          color={getStatusColor(lead.status)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Typography variant="body2" sx={{ mr: 1 }}>
                            {lead.score}
                          </Typography>
                          <LinearProgress
                            variant="determinate"
                            value={lead.score}
                            color={getScoreColor(lead.score)}
                            sx={{ width: 50, height: 4 }}
                          />
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{lead.source}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                          ${lead.estimatedValue.toLocaleString()}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{lead.nextFollowUp}</Typography>
                      </TableCell>
                      <TableCell>
                        <IconButton
                          onClick={(e) => handleMenuOpen(e, lead)}
                          size="small"
                        >
                          <MoreIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
              )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Pagination */}
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={leads.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={(event, newPage) => setPage(newPage)}
            onRowsPerPageChange={(event) => {
              setRowsPerPage(parseInt(event.target.value, 10));
              setPage(0);
            }}
          />
        </CardContent>
      </Card>

      {/* Actions Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={() => handleDetailsOpen(selectedLead)}>
          <ViewIcon sx={{ mr: 1 }} />
          View Details
        </MenuItem>
        <MenuItem>
          <EditIcon sx={{ mr: 1 }} />
          Edit Lead
        </MenuItem>
        <MenuItem>
          <CallIcon sx={{ mr: 1 }} />
          Call Lead
        </MenuItem>
        <MenuItem>
          <EmailIcon sx={{ mr: 1 }} />
          Send Email
        </MenuItem>
      </Menu>

      {/* Lead Details Dialog */}
      <Dialog open={detailsOpen} onClose={() => setDetailsOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Lead Details - {selectedLead?.name}</DialogTitle>
        <DialogContent>
          {selectedLead && (
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>
                  Contact Information
                </Typography>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary">Name</Typography>
                  <Typography>{selectedLead.name}</Typography>
                </Box>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary">Email</Typography>
                  <Typography>{selectedLead.email}</Typography>
                </Box>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary">Phone</Typography>
                  <Typography>{selectedLead.phone}</Typography>
                </Box>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary">Company</Typography>
                  <Typography>{selectedLead.company}</Typography>
                </Box>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>
                  Lead Details
                </Typography>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary">Status</Typography>
                  <Chip
                    label={selectedLead.status}
                    color={getStatusColor(selectedLead.status)}
                    size="small"
                  />
                </Box>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary">Lead Score</Typography>
                  <Typography>{selectedLead.score}/100</Typography>
                </Box>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary">Source</Typography>
                  <Typography>{selectedLead.source}</Typography>
                </Box>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary">Estimated Value</Typography>
                  <Typography>${selectedLead.estimatedValue.toLocaleString()}</Typography>
                </Box>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary">Next Follow-up</Typography>
                  <Typography>{selectedLead.nextFollowUp}</Typography>
                </Box>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>
                  Notes
                </Typography>
                <Typography>{selectedLead.notes}</Typography>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailsOpen(false)}>Close</Button>
          <Button variant="contained">Follow Up</Button>
        </DialogActions>
      </Dialog>

      {/* Add Lead Dialog */}
      <Dialog open={addOpen} onClose={() => setAddOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add New Lead</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Contact Type</InputLabel>
                <Select
                  value={form.type}
                  label="Contact Type"
                  onChange={(e) => setForm(f => ({ ...f, type: e.target.value }))}
                >
                  <MenuItem value="individual">Individual</MenuItem>
                  <MenuItem value="business">Business</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Source</InputLabel>
                <Select
                  value={form.source}
                  label="Source"
                  onChange={(e) => setForm(f => ({ ...f, source: e.target.value }))}
                >
                  <MenuItem value="website">Website</MenuItem>
                  <MenuItem value="social_media">Social Media</MenuItem>
                  <MenuItem value="referral">Referral</MenuItem>
                  <MenuItem value="advertising">Advertising</MenuItem>
                  <MenuItem value="trade_show">Trade Show</MenuItem>
                  <MenuItem value="cold_call">Cold Call</MenuItem>
                  <MenuItem value="email">Email</MenuItem>
                  <MenuItem value="other">Other</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="First Name"
                value={form.firstName}
                onChange={(e) => setForm(f => ({ ...f, firstName: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Last Name"
                value={form.lastName}
                onChange={(e) => setForm(f => ({ ...f, lastName: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={form.email}
                onChange={(e) => setForm(f => ({ ...f, email: e.target.value }))}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Phone"
                placeholder="+1 555 123 4567"
                value={form.phone}
                onChange={(e) => setForm(f => ({ ...f, phone: e.target.value }))}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Company"
                value={form.company}
                onChange={(e) => setForm(f => ({ ...f, company: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Budget (optional)"
                type="number"
                inputProps={{ min: 0 }}
                value={form.budget}
                onChange={(e) => setForm(f => ({ ...f, budget: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Notes"
                multiline
                minRows={3}
                value={form.notes}
                onChange={(e) => setForm(f => ({ ...f, notes: e.target.value }))}
              />
            </Grid>
            {formError && (
              <Grid item xs={12}>
                <FormHelperText error>{formError}</FormHelperText>
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddOpen(false)} disabled={submitting}>Cancel</Button>
          <Button
            variant="contained"
            disabled={submitting}
            onClick={async () => {
              try {
                setFormError('');
                if (!selectedLocation?._id) {
                  setFormError('Please select a location first.');
                  return;
                }
                if (!form.email || !form.phone) {
                  setFormError('Email and phone are required.');
                  return;
                }
                setSubmitting(true);
                const payload = {
                  locationId: selectedLocation._id,
                  contact: {
                    type: form.type,
                    firstName: form.firstName,
                    lastName: form.lastName,
                    email: form.email,
                    phone: form.phone,
                    company: form.company
                  },
                  source: form.source,
                  interest: {
                    budget: form.budget ? Number(form.budget) : undefined
                  },
                  notes: form.notes
                };
                await api.post('/marketing/leads', payload);
                setAddOpen(false);
                setForm({
                  type: 'individual', firstName: '', lastName: '', email: '', phone: '', company: '', source: 'website', budget: '', notes: ''
                });
                await loadLeads();
                await loadStats();
              } catch (e) {
                const msg = e?.response?.data?.message || 'Failed to create lead';
                setFormError(msg);
              } finally {
                setSubmitting(false);
              }
            }}
          >
            {submitting ? 'Creating...' : 'Create Lead'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Leads;
